import { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarCheck, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { submitAppointment } from "@/lib/api";
import { SITE, SERVICE_OPTIONS } from "@/lib/site";

const Ctx = createContext(null);
export const useAppointment = () => useContext(Ctx);

const EMPTY = {
  name: "", email: "", phone: "", service: "", visit_type: "Telehealth",
  preferred_date: "", preferred_time: "Morning", insurance: "", notes: "",
};

export function AppointmentProvider({ children }) {
  const [open, setOpen] = useState(false);
  const openDialog = () => setOpen(true);
  return (
    <Ctx.Provider value={{ openDialog }}>
      {children}
      <Dialog open={open} onClose={() => setOpen(false)} />
    </Ctx.Provider>
  );
}

function Dialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill in your name, email, phone, and service.");
      return;
    }
    setLoading(true);
    try {
      await submitAppointment(form);
      setDone(true);
      toast.success("Request received — we'll be in touch within one business day.");
    } catch {
      toast.error("Something went wrong. Please call us at " + SITE.phone);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setForm(EMPTY); setDone(false); }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="appointment-dialog"
        >
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={close} />
          <motion.div
            className="relative w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl shadow-premium"
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              onClick={close}
              className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full bg-navy-50 text-navy hover:bg-softblue transition-colors"
              aria-label="Close" data-testid="appointment-close-btn"
            >
              <X className="h-5 w-5" />
            </button>

            {done ? (
              <div className="p-10 text-center">
                <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-teal/10 text-teal">
                  <Check className="h-8 w-8" />
                </div>
                <h3 className="font-display text-3xl text-navy">You're all set.</h3>
                <p className="mt-3 text-muted-foreground">
                  Thank you for taking the first step. Our care coordinator will reach out within
                  one business day to confirm your appointment.
                </p>
                <p className="mt-4 text-sm text-muted-foreground">{SITE.crisis}</p>
                <Button onClick={close} className="mt-6 rounded-full bg-teal hover:bg-teal-dark px-8">
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-7 sm:p-9">
                <div className="mb-6 flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-teal/10 text-teal">
                    <CalendarCheck className="h-6 w-6" />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl leading-tight text-navy">Schedule Your Free 15-Minute Consultation</h3>
                    <p className="text-sm text-muted-foreground mt-1">A free, no-pressure call to see if we're the right fit — we reply within one business day.</p>
                  </div>
                </div>

                <div className="grid gap-4">
                  <Field label="Full name" required>
                    <Input data-testid="appt-name" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Jane Doe" className="h-12 rounded-xl" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email" required>
                      <Input data-testid="appt-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="jane@email.com" className="h-12 rounded-xl" />
                    </Field>
                    <Field label="Phone" required>
                      <Input data-testid="appt-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="(860) 555-0123" className="h-12 rounded-xl" />
                    </Field>
                  </div>
                  <Field label="What are you seeking help with?" required>
                    <Select value={form.service} onValueChange={set("service")}>
                      <SelectTrigger data-testid="appt-service" className="h-12 rounded-xl"><SelectValue placeholder="Select a service" /></SelectTrigger>
                      <SelectContent className="z-[200]">
                        {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Visit type">
                      <Select value={form.visit_type} onValueChange={set("visit_type")}>
                        <SelectTrigger data-testid="appt-visit" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[200]">
                          <SelectItem value="Telehealth">Telehealth (Video)</SelectItem>
                          <SelectItem value="In-Person">In-Person (Southington)</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Preferred time">
                      <Select value={form.preferred_time} onValueChange={set("preferred_time")}>
                        <SelectTrigger data-testid="appt-time" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[200]">
                          <SelectItem value="Morning">Morning</SelectItem>
                          <SelectItem value="Afternoon">Afternoon</SelectItem>
                          <SelectItem value="Evening">Evening</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Preferred date">
                      <Input data-testid="appt-date" type="date" value={form.preferred_date} onChange={(e) => set("preferred_date")(e.target.value)} className="h-12 rounded-xl" />
                    </Field>
                    <Field label="Insurance (optional)">
                      <Input data-testid="appt-insurance" value={form.insurance} onChange={(e) => set("insurance")(e.target.value)} placeholder="e.g. Aetna" className="h-12 rounded-xl" />
                    </Field>
                  </div>
                  <Field label="Anything you'd like us to know? (optional)">
                    <Textarea data-testid="appt-notes" value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} className="rounded-xl resize-none" />
                  </Field>
                </div>

                <Button type="submit" disabled={loading} data-testid="appt-submit"
                  className="mt-6 w-full h-13 py-6 rounded-full bg-teal hover:bg-teal-dark text-white text-base font-medium">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request My Free Consultation"}
                </Button>
                <p className="mt-4 text-center text-xs text-muted-foreground">{SITE.crisis}</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-sm text-navy/80">
        {label} {required && <span className="text-teal">*</span>}
      </Label>
      {children}
    </div>
  );
}
